/**
 * Tiny Web-Audio-API chime helper.
 *
 * Used by the dental Tooth Sparkle micro-interaction and the
 * prescription-saved / visit-completed flows. Subtle, ~120 ms,
 * never plays before a user gesture so browsers don't block it.
 *
 * Respects the in-app "Operatory Mode" silent preference and the
 * OS-level `prefers-reduced-motion` media query — both turn it off.
 */

const SILENT_KEY = 'np.silentChimes';

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  const AudioCtor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtor) return null;
  try {
    ctx = new AudioCtor();
    return ctx;
  } catch {
    return null;
  }
}

export function setSilentChimes(silent: boolean): void {
  try {
    localStorage.setItem(SILENT_KEY, silent ? '1' : '0');
  } catch {
    // ignore — private browsing
  }
}

export function silentChimesEnabled(): boolean {
  try {
    return localStorage.getItem(SILENT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Play a short pleasant tone. Type controls the pitch + envelope:
 *   - 'sparkle'  — bright two-note arpeggio, used by ToothChart
 *   - 'success'  — single warm tone, used on prescription save
 */
export function playChime(type: 'sparkle' | 'success' = 'sparkle'): void {
  if (silentChimesEnabled()) return;
  if (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ) {
    // Users who asked for less motion typically also want less audio
    // distraction. Skip.
    return;
  }

  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    // Will succeed on the next user-gesture cycle; do nothing this turn.
    c.resume().catch(() => {});
    return;
  }

  const now = c.currentTime;
  const master = c.createGain();
  master.gain.setValueAtTime(0, now);
  master.gain.linearRampToValueAtTime(0.08, now + 0.01);
  master.connect(c.destination);

  if (type === 'sparkle') {
    // Two-note bright arpeggio: E6 → B6
    [659.25, 987.77].forEach((freq, i) => {
      const osc = c.createOscillator();
      const env = c.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + i * 0.04);
      env.gain.setValueAtTime(0, now + i * 0.04);
      env.gain.linearRampToValueAtTime(0.9, now + i * 0.04 + 0.012);
      env.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.04 + 0.18);
      osc.connect(env).connect(master);
      osc.start(now + i * 0.04);
      osc.stop(now + i * 0.04 + 0.2);
    });
    master.gain.linearRampToValueAtTime(0, now + 0.3);
  } else {
    // Single warm tone (success)
    const osc = c.createOscillator();
    osc.type = 'triangle';
    osc.frequency.setValueAtTime(523.25, now); // C5
    osc.frequency.exponentialRampToValueAtTime(659.25, now + 0.14); // → E5
    osc.connect(master);
    osc.start(now);
    osc.stop(now + 0.25);
    master.gain.linearRampToValueAtTime(0, now + 0.25);
  }
}
